import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { CURRENCIES, fmtMoney, currencyInfo } from "@/lib/currency";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Printer, User } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function InvoiceGenerator() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("invoice"); // 'invoice' or 'quote'
  const [number, setNumber] = useState(`INV-${Date.now().toString().slice(-6)}`);
  const [date, setDate] = useState(today());
  const [dueDate, setDueDate] = useState(today());
  const [fromName, setFromName] = useState(user?.username ? `${user.username}'s Bakery` : "Your Bakery");
  const [fromDetails, setFromDetails] = useState("hello@yourbakery.com");
  const [toName, setToName] = useState("");
  const [toDetails, setToDetails] = useState("");
  const [items, setItems] = useState([{ id: 1, desc: "Dozen sourdough cake pops", qty: 2, price: 24 }]);
  const [nextId, setNextId] = useState(2);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("Thank you for your order!");
  const [currency, setCurrency] = useState(user?.currency || "USD");

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    if (user.currency) setCurrency(user.currency);
  }, [user, loading, nav]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + (parseFloat(i.qty) || 0) * (parseFloat(i.price) || 0), 0), [items]);
  const tax = subtotal * ((parseFloat(taxRate) || 0) / 100);
  const total = subtotal + tax;

  const addItem = () => { setItems([...items, { id: nextId, desc: "", qty: 1, price: 0 }]); setNextId(nextId + 1); };
  const updateItem = (id, patch) => setItems(items.map((i) => i.id === id ? { ...i, ...patch } : i));
  const removeItem = (id) => setItems(items.filter((i) => i.id !== id));

  const doPrint = () => window.print();

  if (loading || !user) return null;
  const label = mode === "quote" ? "Quote" : "Invoice";

  return (
    <>
      <div className="print:hidden"><Header /></div>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 print:hidden flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><FileText className="w-3.5 h-3.5" style={{ color: "#2F80ED" }} /><span>Business tool</span></div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">Invoice & <span style={{ color: "#2F80ED" }}>Quote</span> Generator</h1>
            <p className="mt-2 text-[#857163] max-w-2xl">Fill in the details below, then print or save as PDF from your browser (⌘/Ctrl-P).</p>
          </div>
          <div className="flex gap-2 items-center">
            <div className="inline-flex rounded-full border border-[#EBDCD1] p-0.5 bg-white">
              {[{k:"invoice",l:"Invoice"},{k:"quote",l:"Quote"}].map((m) => (
                <button key={m.k} onClick={() => setMode(m.k)} className={`px-3 py-1 rounded-full text-xs font-semibold ${mode === m.k ? "bg-[#3B2C24] text-white" : "text-[#857163]"}`} data-testid={`mode-${m.k}`}>{m.l}</button>
              ))}
            </div>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="cp-input !py-1.5 !text-xs max-w-[130px]" data-testid="currency-select">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
            </select>
            <button onClick={doPrint} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 transition" style={{ background: "#2F80ED" }} data-testid="print-btn"><Printer className="w-4 h-4" /> Print / PDF</button>
          </div>
        </div>

        {/* Editable form (hidden in print) */}
        <div className="cp-card p-5 sm:p-6 mb-6 print:hidden space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <label><span className="text-xs text-[#857163]">{label} #</span><input className="cp-input mt-1" value={number} onChange={(e) => setNumber(e.target.value)} data-testid="number-input" /></label>
            <label><span className="text-xs text-[#857163]">Date</span><input type="date" className="cp-input mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-input" /></label>
            <label><span className="text-xs text-[#857163]">Due date</span><input type="date" className="cp-input mt-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="due-input" /></label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-[#857163] mb-1">From</div>
              <input className="cp-input" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Business name" data-testid="from-name" />
              <textarea className="cp-input mt-1 min-h-[64px] text-sm" value={fromDetails} onChange={(e) => setFromDetails(e.target.value)} placeholder="Address, email, phone" data-testid="from-details" />
            </div>
            <div>
              <div className="text-xs text-[#857163] mb-1">Bill to</div>
              <input className="cp-input" value={toName} onChange={(e) => setToName(e.target.value)} placeholder="Customer name" data-testid="to-name" />
              <textarea className="cp-input mt-1 min-h-[64px] text-sm" value={toDetails} onChange={(e) => setToDetails(e.target.value)} placeholder="Address, email, phone" data-testid="to-details" />
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-[#3B2C24] mb-2">Line items</div>
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} className="grid grid-cols-[1fr_80px_100px_36px] gap-2 items-center" data-testid={`line-${it.id}`}>
                  <input className="cp-input !py-1.5 !text-sm" value={it.desc} onChange={(e) => updateItem(it.id, { desc: e.target.value })} placeholder="Description" />
                  <input type="number" step={1} min={0} className="cp-input !py-1.5 !text-sm" value={it.qty} onChange={(e) => updateItem(it.id, { qty: e.target.value })} placeholder="Qty" />
                  <input type="number" step={0.01} min={0} className="cp-input !py-1.5 !text-sm" value={it.price} onChange={(e) => updateItem(it.id, { price: e.target.value })} placeholder="Price" />
                  <button onClick={() => removeItem(it.id)} className="grid place-items-center rounded-full hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={addItem} className="text-sm text-[#857163] hover:bg-[#F9EFE6] py-2 w-full rounded-2xl border border-dashed border-[#EBDCD1] inline-flex items-center justify-center gap-1.5" data-testid="add-line"><Plus className="w-3.5 h-3.5" /> Add item</button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label><span className="text-xs text-[#857163]">Tax rate (%)</span><input type="number" step={0.1} min={0} className="cp-input mt-1" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="tax-rate" /></label>
            <label><span className="text-xs text-[#857163]">Footer notes</span><input className="cp-input mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="notes" /></label>
          </div>
        </div>

        {/* Printable preview */}
        <div id="invoice-print" className="bg-white rounded-3xl p-8 border border-[#EBDCD1] print:border-0 print:rounded-none print:p-6" data-testid="invoice-preview">
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold text-[#3B2C24]">{label.toUpperCase()}</h1>
              <div className="text-sm text-[#857163] mt-1">#{number}</div>
            </div>
            <div className="text-right text-sm">
              <div className="font-semibold text-[#3B2C24]">{fromName}</div>
              <div className="text-[#857163] whitespace-pre-wrap">{fromDetails}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6 mb-8 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#857163] mb-1">Bill to</div>
              <div className="font-semibold text-[#3B2C24]">{toName || "—"}</div>
              <div className="text-[#857163] whitespace-pre-wrap">{toDetails}</div>
            </div>
            <div className="text-right">
              <div className="text-[#857163]">Date: <span className="text-[#3B2C24]">{date}</span></div>
              {mode === "invoice" && <div className="text-[#857163]">Due: <span className="text-[#3B2C24]">{dueDate}</span></div>}
            </div>
          </div>
          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="border-b-2 border-[#3B2C24] text-left">
                <th className="py-2">Description</th>
                <th className="py-2 text-right w-16">Qty</th>
                <th className="py-2 text-right w-24">Price</th>
                <th className="py-2 text-right w-28">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#EBDCD1]">
                  <td className="py-2">{it.desc || "—"}</td>
                  <td className="py-2 text-right tabular-nums">{it.qty}</td>
                  <td className="py-2 text-right tabular-nums">{fmtMoney(parseFloat(it.price) || 0, currency)}</td>
                  <td className="py-2 text-right tabular-nums font-semibold">{fmtMoney((parseFloat(it.qty) || 0) * (parseFloat(it.price) || 0), currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{fmtMoney(subtotal, currency)}</span></div>
              {taxRate > 0 && <div className="flex justify-between"><span>Tax ({taxRate}%)</span><span className="tabular-nums">{fmtMoney(tax, currency)}</span></div>}
              <div className="flex justify-between border-t-2 border-[#3B2C24] pt-1 text-lg font-bold text-[#3B2C24]"><span>Total</span><span className="tabular-nums">{fmtMoney(total, currency)}</span></div>
            </div>
          </div>
          {notes && <div className="mt-8 pt-4 border-t border-[#EBDCD1] text-sm text-[#857163] italic">{notes}</div>}
        </div>
      </main>
    </>
  );
}
